import { useState } from 'react'

import { serviceAssets } from '../../../constants/assets'
import type { ServiceRecord } from '../../../types'
import { ActiveBadge } from './ServiceCardParts'
import { ServiceToggle } from './ServiceToggle'

type ServiceCardProps = {
  service: ServiceRecord
  onToggleActive: (id: string) => void
  onEdit: (id: string) => void
  onView: (id: string) => void
  isUpdating: boolean
}

export const ServiceCard = ({
  service,
  onToggleActive,
  onEdit,
  onView,
  isUpdating,
}: ServiceCardProps) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <article
      className={`relative w-[299px] shrink-0 overflow-hidden rounded-[36px] border-[0.8px] border-solid border-dash-lime bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.1),0px_1px_2px_-1px_rgba(0,0,0,0.1)] ${
        expanded ? 'h-auto min-h-[326px]' : 'h-[326px]'
      }`}
    >
      <span aria-hidden className="absolute left-0 top-0 h-full w-[4px] bg-dash-lime" />

      <div className="flex h-full flex-col gap-[13px] px-[16px] pb-[16px] pt-[28px]">
        <div className="flex h-[24px] items-start justify-between">
          <span className="rounded-full border border-solid border-white bg-dash-lime px-[10.8px] py-[2.8px] font-host text-[10px] font-semibold uppercase leading-[15px] tracking-[0.5px] text-black">
            {service.badge}
          </span>

          <div className="flex items-center gap-[9px]">
            <button
              type="button"
              aria-label={`Edit ${service.title}`}
              onClick={() => onEdit(service.id)}
              className="block size-[18px] shrink-0 transition-opacity hover:opacity-80 motion-reduce:transition-none"
            >
              <img alt="" aria-hidden className="block size-full max-w-none" src={serviceAssets.edit} />
            </button>

            {/* <button
              type="button"
              aria-label={`View ${service.title}`}
              onClick={() => onView(service.id)}
              className="flex size-[18px] shrink-0 flex-col items-center justify-center overflow-hidden rounded-[4px] border border-solid border-dash-hairline-soft px-[2px] py-[5px] transition-opacity hover:opacity-80 motion-reduce:transition-none"
            >
              <span aria-hidden className="relative block h-[7.096px] w-[10px]">
                <span className="absolute inset-[-8.44%_-6%_-8.47%_-6%] block">
                  <img alt="" className="block size-full max-w-none" src={serviceAssets.view} />
                </span>
              </span>
            </button> */}
          </div>
        </div>

        <div className="flex items-start gap-[6px]">
          <img
            alt=""
            aria-hidden
            className="block h-[19px] w-[20px] max-w-none shrink-0"
            src={serviceAssets.route}
          />
          <p className="font-host text-[14px] font-normal leading-normal text-dash-svc-route">
            {service.route}
          </p>
        </div>

        <h3 className="font-host text-[16px] font-semibold leading-[1.2] text-black">
          {service.title}
        </h3>

        <div>
          <p
            className={`font-host text-[14px] font-normal leading-[20px] text-dash-svc-desc ${
              expanded ? '' : 'line-clamp-2'
            }`}
          >
            {service.description}
          </p>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            className="mt-1 font-host text-[13px] font-medium text-black underline decoration-solid underline-offset-auto"
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        </div>

        <div className="font-host font-medium leading-normal text-black">
          <p className="text-[12px]">Price</p>
          <p className="text-[16px] tracking-[-0.48px]">€{service.priceEur}</p>
        </div>

        <div className="mt-auto border-t border-solid border-dash-svc-footer-rule pt-[14px]">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-[8px]">
              <span className="font-host text-[12px] font-medium leading-[16px] text-black">
                Status
              </span>
              <ActiveBadge active={service.active} />
            </div>

            <ServiceToggle
              active={service.active}
              label={service.title}
              disabled={isUpdating}
              onChange={() => onToggleActive(service.id)}
            />
          </div>
        </div>
      </div>
    </article>
  )
}
